<template>
  <data-table min-width="1300px" class="ai-visibility-competitors-table">
    <template #head>
      <data-table-row header fill>
        <data-table-cell
            v-for="column in columns"
            :key="column.key"
            :width="column.width"
            :align="column.align || 'left'"
            muted
            header
        >
          <button
              v-if="column.sortable"
              type="button"
              class="ai-visibility-competitors-table__sort"
              @click="handleSort(column.key)"
          >
            <span>{{ column.label }}</span>
            <i class="material-icons">{{ sortIcon(column.key) }}</i>
          </button>
          <span v-else>{{ column.label }}</span>
        </data-table-cell>
      </data-table-row>
    </template>

    <template #body>
      <template v-if="rows.length">
        <data-table-row
            v-for="row in rows"
            :key="`competitor-${row.domain}-${row.rank}`"
            clickable
            @click="$emit('select-competitor', row)"
        >
          <data-table-cell width="500px" class="right-border">
            <competitor-brand-cell :row="row"/>
          </data-table-cell>
<!--          <data-table-cell width="240px">-->
<!--            <div-->
<!--                class="ai-visibility-competitors-table__share"-->
<!--                :class="{-->
<!--                'ai-visibility-competitors-table__share&#45;&#45;tracked': row.isTrackedDomain,-->
<!--              }"-->
<!--            >-->
<!--              <div class="ai-visibility-competitors-table__metric-line">-->
<!--                <strong>{{ row.sharePercent }}%</strong>-->
<!--                <span>{{ row.mentionCountLabel }}</span>-->
<!--              </div>-->
<!--              <score-bar-->
<!--                  :value="row.sharePercent"-->
<!--                  :show-value="false"-->
<!--                  tone="neutral"-->
<!--              />-->
<!--            </div>-->
<!--          </data-table-cell>-->
          <data-table-cell width="210px" align="center">
            <div class="ai-visibility-competitors-table__stacked-metric">
              <strong>{{ row.mentionRatePercent }}%</strong>
              <span>{{ row.mentionedAnswersLabel }}</span>
            </div>
          </data-table-cell>
          <data-table-cell width="160px" align="center">
            <div class="ai-visibility-competitors-table__stacked-metric">
              <strong>{{ row.averagePositionLabel }}</strong>
              <span>1 = top</span>
            </div>
          </data-table-cell>
          <data-table-cell width="230px" align="center">
            <status-pill
                :tone="row.headToHead.tone"
                :label="row.headToHead.label"
                :show-arrow="true"
            />
          </data-table-cell>
        </data-table-row>
      </template>
      <div v-else class="ai-visibility-competitors-table__empty">
        <empty-state
            title="No competitors found"
            description="Try a different search term."
        />
      </div>
    </template>
  </data-table>
</template>

<script>
import DataTable from '../../../ui/table/DataTable.vue'
import DataTableRow from '../../../ui/table/DataTableRow.vue'
import DataTableCell from '../../../ui/table/DataTableCell.vue'
// import ScoreBar from '../../../ui/display/ScoreBar.vue'
import StatusPill from '../../../ui/display/StatusPill.vue'
import EmptyState from '../../../ui/states/EmptyState.vue'
import CompetitorBrandCell from './CompetitorBrandCell.vue'

const COLUMNS = [
  {key: 'brand', label: 'Brand', width: '500px'},
  // {key: 'share', label: 'Share of mentions', width: '240px'},
  {key: 'mentionRate', label: 'Mention rate', width: '210px', align: 'center', sortable: true},
  {key: 'avgRank', label: 'Avg. rank', width: '160px', align: 'center', sortable: true},
  {key: 'headToHead', label: 'Head-to-head', width: '230px', align: 'center', sortable: true}
]

export default {
  name: 'CompetitorsTable',
  components: {
    DataTable,
    DataTableRow,
    DataTableCell,
    // ScoreBar,
    StatusPill,
    EmptyState,
    CompetitorBrandCell,
  },
  props: {
    rows: {
      type: Array,
      required: true,
    },
    sortKey: {
      type: String,
      required: true,
    },
    sortDirection: {
      type: String,
      required: true,
    },
  },
  computed: {
    columns() {
      return COLUMNS
    },
  },
  methods: {
    handleSort(key) {
      const defaultDirection = key === 'avgRank' ? 'asc' : 'desc'
      const direction = this.sortKey === key
        ? this.toggleDirection(this.sortDirection)
        : defaultDirection

      this.$emit('sort-change', {key, direction})
    },
    toggleDirection(direction) {
      return direction === 'desc' ? 'asc' : 'desc'
    },
    sortIcon(key) {
      if (this.sortKey !== key) {
        return 'unfold_more'
      }
      return this.sortDirection === 'desc' ? 'expand_more' : 'expand_less'
    },
  },
}
</script>

<style lang="scss" scoped>

.ai-visibility-competitors-table {
  border-right: 0;
  border-left: 0;
  border-radius: 0;

  ::v-deep .ai-visibility-data-table-row:last-child::after {
    border-bottom: none;
  }

}

.ai-visibility-competitors-table__sort {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  font-weight: 800;
  padding: 0;
}

.ai-visibility-competitors-table__sort:focus {
  outline: 2px solid var(--v-lightYellowColor-base);
  outline-offset: 2px;
}

.ai-visibility-competitors-table__sort i {
  font-size: 18px;
}

.ai-visibility-competitors-table ::v-deep .ai-visibility-data-table-cell {
  align-content: center;
}

.ai-visibility-competitors-table__empty {
  padding: 18px;
}

.ai-visibility-competitors-table__share,
.ai-visibility-competitors-table__stacked-metric {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ai-visibility-competitors-table__share--tracked ::v-deep .ai-visibility-score-bar__fill {
  background: var(--v-profileAnchorColor-base);
}

.ai-visibility-competitors-table__metric-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.ai-visibility-competitors-table__metric-line strong,
.ai-visibility-competitors-table__stacked-metric strong {
  color: var(--v-mainColor-base);
  font-size: 16px;
  font-weight: 400;
  line-height: 1.25;
}

.ai-visibility-competitors-table__metric-line span,
.ai-visibility-competitors-table__stacked-metric span {
  color: var(--v-darkGrayColor-base);
  font-size: 12px;
  font-weight: 400;
  line-height: 1.35;
}

.ai-visibility-competitors-table__stacked-metric span {
  overflow-wrap: anywhere;
}

.ai-visibility-competitors-table ::v-deep .ai-visibility-status-pill {
  max-width: 100%;
  white-space: normal;
}

</style>
